import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface PageHeroProps {
    eyebrow?: ReactNode
    title: ReactNode
    description?: ReactNode
    actions?: ReactNode
    media?: ReactNode
}

export const PageHero: FC<PageHeroProps> = ({ eyebrow, title, description, actions, media }): ReactNode => {
    return (
        <section id='page-intro' className='relative overflow-hidden bg-linear-to-b from-lavender/80 via-lavender/30 to-shell'>
            <div aria-hidden='true' className='pointer-events-none absolute -right-44 -top-52 size-[36rem] rounded-full bg-lavender blur-3xl' />

            <div className={cn('relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-16 pt-12 sm:px-8 sm:pb-24 sm:pt-16 lg:gap-16', media && 'lg:grid-cols-12')}>
                <div className={cn('animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out motion-reduce:animate-none', media ? 'lg:col-span-7' : 'max-w-3xl')}>
                    {eyebrow && (
                        <div className='mb-6'>
                            {eyebrow}
                        </div>
                    )}

                    <h1 className='font-heading text-[2.5rem] leading-[1.08] text-plum text-balance sm:text-5xl lg:text-[3.6rem]'>
                        {title}
                    </h1>

                    {description && (
                        <div className='mt-6 max-w-2xl text-lg leading-relaxed text-espresso-soft sm:text-xl sm:leading-relaxed'>
                            {description}
                        </div>
                    )}

                    {actions && (
                        <div className='mt-9 flex flex-wrap items-center gap-4'>
                            {actions}
                        </div>
                    )}
                </div>

                {media && (
                    <div className='animate-in fade-in zoom-in-95 duration-1000 ease-out motion-reduce:animate-none lg:col-span-5'>
                        {media}
                    </div>
                )}
            </div>
        </section>
    )
}

interface HeroImageProps {
    src: string
    alt: string
    aspectClassName?: string
    imageClassName?: string
}

export const HeroImage: FC<HeroImageProps> = ({ src, alt, aspectClassName = 'aspect-[4/5]', imageClassName }): ReactNode => {
    return (
        <div className='relative mx-auto w-full max-w-md'>
            <div aria-hidden='true' className='absolute -bottom-5 -left-5 h-full w-full rounded-[2rem] bg-honey/45' />

            <div className={cn('relative overflow-hidden rounded-[2rem] shadow-2xl shadow-plum/15', aspectClassName)}>
                <Image
                    src={src}
                    alt={alt}
                    fill
                    preload
                    sizes='(min-width: 1024px) 34vw, (min-width: 640px) 28rem, 100vw'
                    className={cn('object-cover', imageClassName)}
                />
            </div>
        </div>
    )
}

interface HeroPillProps {
    children: ReactNode
}

export const HeroPill: FC<HeroPillProps> = ({ children }): ReactNode => {
    return (
        <p className='inline-flex items-center gap-2.5 rounded-full bg-shell/80 px-4 py-1.5 text-sm font-medium text-plum ring-1 ring-plum/10'>
            <span className='size-1.5 rounded-full bg-honey' aria-hidden='true' />
            {children}
        </p>
    )
}
